import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Button, Spin, Modal, Radio, Space } from 'antd';
import { UndoOutlined, ClearOutlined, AppstoreOutlined, FundOutlined, DownloadOutlined } from '@ant-design/icons';
// 直接导入AddDataModal组件，避免懒加载导致的问题
import AddDataModal from './components/AddDataModal';
import './App.css';

// 动态导入组件，减少初始加载体积
const PipelineGraph = lazy(() => import('./components/PipelineGraph'));
const DataTable = lazy(() => import('./components/DataTable'));

const App = () => {
  // 存储管线数据
  const [pipelineData, setPipelineData] = useState([]);
  const [nextNodeId, setNextNodeId] = useState(1);
  const [lastMainNode, setLastMainNode] = useState(null);
  const [lastFormData, setLastFormData] = useState({});
  const [isTableView, setIsTableView] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isViewSwitching, setIsViewSwitching] = useState(false);
  const [isLowPerformanceBrowser, setIsLowPerformanceBrowser] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  // 添加引用Pipeline组件的ref
  const pipelineGraphRef = useRef(null);
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('添加管线数据');
  const [editingNode, setEditingNode] = useState(null);
  const [appReady, setAppReady] = useState(false);
  
  // 添加状态来跟踪是否是通过"新建线路"按钮创建的
  const [isNewLineCreation, setIsNewLineCreation] = useState(false);
  
  // 添加键盘事件监听器，检测+键按下时添加节点（仅限电脑端）
  useEffect(() => {
    // 只有在非移动设备上才添加快捷键
    if (!isMobile) {
      // 用于跟踪按键状态的变量
      let plusKeyPressed = false;
      
      const handleKeyDown = (event) => {
        // 检测+键（小键盘的+或主键盘的+）
        // 小键盘 + 的键码是107，数字行的 + 键码是187
        if ((event.keyCode === 107 || (event.keyCode === 187 && event.shiftKey)) && 
            // 确保不是在输入框中按下的+键
            !(document.activeElement.tagName === 'INPUT' || 
              document.activeElement.tagName === 'TEXTAREA') &&
            // 确保模态框没有打开
            !modalVisible) {
          
          // 标记按键已按下，防止重复触发
          if (!plusKeyPressed) {
            plusKeyPressed = true;
            showAddModal();
            
            // 阻止事件冒泡，避免+号输入到井深输入框
            event.preventDefault();
            event.stopPropagation();
          }
        }
      };
      
      const handleKeyUp = (event) => {
        // +键释放时重置状态
        if (event.keyCode === 107 || (event.keyCode === 187 && event.shiftKey)) {
          plusKeyPressed = false;
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }
  }, [isMobile, modalVisible]);
  
  // 检测低性能浏览器
  useEffect(() => {
    // 获取浏览器信息
    const ua = navigator.userAgent.toLowerCase();
    let isLowPerformance = false;
    
    // 检测国产浏览器和内置浏览器
    if (
      ua.indexOf('quark') > -1 || // 夸克浏览器
      ua.indexOf('ucbrowser') > -1 || // UC浏览器
      ua.indexOf('ucweb') > -1 || // UC浏览器
      ua.indexOf('micromessenger') > -1 || // 微信内置浏览器
      ua.indexOf('mqqbrowser') > -1 || // QQ浏览器
      ua.indexOf('miuibrowser') > -1 || // 小米浏览器
      ua.indexOf('huaweibrowser') > -1 // 华为浏览器
    ) {
      isLowPerformance = true;
      
      // 添加特定浏览器的CSS类
      if(ua.indexOf('quark') > -1) {
        document.body.classList.add('browser-quark');
      } else if(ua.indexOf('ucbrowser') > -1 || ua.indexOf('ucweb') > -1) {
        document.body.classList.add('browser-uc');
      } else if(ua.indexOf('mqqbrowser') > -1) {
        document.body.classList.add('browser-qq');
      } else if(ua.indexOf('miuibrowser') > -1) {
        document.body.classList.add('browser-miui');
        // 为小米浏览器添加额外的类，用于特殊文件处理
        document.body.classList.add('browser-file-download-fix');
      }
    }
    
    if (isLowPerformance) {
      // 添加低性能浏览器标记
      document.body.classList.add('low-performance-browser');
      // 设置状态
      setIsLowPerformanceBrowser(true);
      
      // 默认使用表格视图，减轻图形计算负担
      if (isMobile) {
        // setIsTableView(true);
      }
    }
  }, [isMobile]);
  
  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 优化的移动端输入法处理
  useEffect(() => {
    if (isMobile) {
      // 优化输入法弹出时的体验
      const handleFocus = (e) => {
        // 检查是否是输入元素
        if (e.target.tagName === 'INPUT' || 
            e.target.tagName === 'TEXTAREA' || 
            e.target.tagName === 'SELECT') {
          document.body.classList.add('input-active');
          setIsKeyboardVisible(true);
          
          // 使用requestAnimationFrame确保滚动发生在下一帧
          requestAnimationFrame(() => {
            // 滚动到视图中，确保输入框可见
            if (e.target.scrollIntoView) {
              e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        }
      };
      
      const handleBlur = (e) => {
        // 只有当没有其他输入元素获得焦点时，才移除input-active类
        if (e.target.tagName === 'INPUT' || 
            e.target.tagName === 'TEXTAREA' || 
            e.target.tagName === 'SELECT') {
          
          // 延迟移除类，以防止在切换输入字段时闪烁
          setTimeout(() => {
            const activeElement = document.activeElement;
            const isInputActive = activeElement && 
                                (activeElement.tagName === 'INPUT' || 
                                 activeElement.tagName === 'TEXTAREA' || 
                                 activeElement.tagName === 'SELECT');
            
            if (!isInputActive) {
              document.body.classList.remove('input-active');
              setIsKeyboardVisible(false);
            }
          }, 100);
        }
      };
      
      // 监听所有输入框的获焦和失焦事件
      document.addEventListener('focusin', handleFocus, { passive: true });
      document.addEventListener('focusout', handleBlur, { passive: true });
      
      return () => {
        document.removeEventListener('focusin', handleFocus);
        document.removeEventListener('focusout', handleBlur);
      };
    }
  }, [isMobile]);
  
  // 从本地存储读取数据
  useEffect(() => {
    const fetchData = () => {
      try {
        const savedData = localStorage.getItem('pipelineData');
        const savedLastFormData = localStorage.getItem('lastFormData');
        
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setPipelineData(parsedData);
            
            // 找到最后一个主节点
            const mainNodes = parsedData.filter(node => node.isMainNode);
            if (mainNodes.length > 0) {
              setLastMainNode(mainNodes[mainNodes.length - 1]);
            }
            
            // 计算下一个ID
            updateNextNodeId(parsedData);
          } catch (e) {
            console.error('加载数据出错:', e);
          }
        }
        
        if (savedLastFormData) {
          try {
            setLastFormData(JSON.parse(savedLastFormData));
          } catch (e) {
            console.error('加载表单数据出错:', e);
          }
        }
        
        // 标记应用已准备好
        setAppReady(true);
      } catch (error) {
        console.error('数据加载失败:', error);
        setAppReady(true); // 即使出错也标记应用已准备好
      }
    };
    
    // 使用setTimeout延迟加载数据，减轻初始化负担
    setTimeout(fetchData, 100);
  }, []);
  
  // 保存数据到本地存储
  useEffect(() => {
    if (pipelineData.length > 0) {
      // 延迟保存，避免频繁写入
      const saveTimer = setTimeout(() => {
        try {
          localStorage.setItem('pipelineData', JSON.stringify(pipelineData));
        } catch (error) {
          console.error('保存数据失败:', error);
        }
      }, 500);
      
      return () => clearTimeout(saveTimer);
    }
  }, [pipelineData]);
  
  // 计算下一个节点ID
  const updateNextNodeId = (data) => {
    if (data.length === 0) {
      setNextNodeId(1);
      return;
    }
    
    // 找出最后一个节点ID
    const lastNode = getLastMainNode(data);
    if (!lastNode) {
      setNextNodeId(1);
      return;
    }
    
    // 解析节点ID
    const nodeId = lastNode.id;
    
    // 尝试提取前缀和数字部分
    const match = nodeId.match(/^([a-zA-Z]*)(\d+)$/);
    
    if (match) {
      // 如果匹配到前缀+数字的格式
      const prefix = match[1]; // 字母前缀
      const numPart = parseInt(match[2]); // 数字部分
      
      // 设置下一个ID为前缀+数字部分+1
      setNextNodeId(`${prefix}${numPart + 1}`);
    } else {
      // 如果没有匹配到前缀+数字的格式，尝试直接解析为数字
      const id = parseInt(nodeId);
      if (!isNaN(id)) {
        setNextNodeId(id + 1);
      } else {
        // 如果解析失败，使用默认值1
        setNextNodeId(1);
      }
    }
  };
  
  // 计算节点位置
  const calculateNodePosition = () => {
    // 首个节点在画布中心点位置
    const CANVAS_WIDTH = 5000;
    const CANVAS_HEIGHT = 2000;
    const CANVAS_CENTER_X = CANVAS_WIDTH / 2;
    const CANVAS_CENTER_Y = CANVAS_HEIGHT / 2;
    
    if (pipelineData.length === 0) {
      return { x: CANVAS_CENTER_X, y: CANVAS_CENTER_Y };
    } 
    // 其他节点在右侧
    else {
      // 获取最后一个添加的节点的位置
      const mainNodes = pipelineData.filter(node => node.isMainNode);
      if (mainNodes.length > 0) {
        const lastMainNode = mainNodes[mainNodes.length - 1];
        // 在最后一个节点的右侧
        return { x: lastMainNode.x + 200, y: lastMainNode.y };
      } else {
        return { x: CANVAS_CENTER_X, y: CANVAS_CENTER_Y };
      }
    }
  };
  
  // 打开添加数据模态框
  const showAddModal = () => {
    setModalTitle('添加管线数据');
    setEditingNode(null);
    setModalVisible(true);
  };
  
  // 打开编辑数据模态框
  const handleNodeClick = (node) => {
    setModalTitle('编辑管线数据');
    setEditingNode(node);
    setModalVisible(true);
  };
  
  // 将CM转换为M - 处理深度字段
  const convertCmToM = (value) => {
    if (!value) return undefined;
    // 确保是数字，然后除以100转换为米
    const cmValue = parseFloat(value);
    if (isNaN(cmValue)) return value;
    // 转换为米，保留2位小数，解决精度问题
    const result = (cmValue / 100);
    return parseFloat(result.toFixed(2)).toString();
  };
  
  // 将M转换为CM - 处理深度字段
  const convertMToCm = (value) => {
    if (!value) return undefined;
    // 确保是数字，然后乘以100转换为厘米
    const mValue = parseFloat(value);
    if (isNaN(mValue)) return value;
    // 转换为厘米，保留0位小数，解决精度问题
    return Math.round(mValue * 100).toString();
  };
  
  // 显示"添加新线路"对话框
  const showNewLineModal = () => {
    // 创建与showAddModal相似的功能，但表单中不填写左埋深
    setModalTitle('添加新线路');
    setEditingNode(null);
    
    // 设置新的lastFormData，确保左埋深为空
    const newLineFormData = { ...lastFormData };
    delete newLineFormData.leftDepth;
    setLastFormData(newLineFormData);
    
    // 添加标记，表示这是通过"新建线路"按钮创建的
    setModalVisible(true);
    setIsNewLineCreation(true);
  };
  
  // 处理表单提交
  const handleFormSubmit = (formData) => {
    // 检查是否创建新线路（没有填写左埋深但有现有节点）
    const isNewLine = !formData.leftDepth && pipelineData.length > 0;
    
    // 如果是创建新线路，显示确认提示
    if (isNewLine && !editingNode) {
      if (!window.confirm('您没有填写左埋深，将创建一条新的线路，不连接到现有管线。是否继续？')) {
        return; // 用户取消，中断表单提交
      }
    }
    
    // 保存常用表单数据
    const newLastFormData = { ...formData };
    delete newLastFormData.id;
    delete newLastFormData.key;
    
    // 检查断面尺寸是否包含星号，确定敷设方式
    let layingMethodValue = formData.layingMethod;
    if (!layingMethodValue) {
      // 总孔数为1且已用孔数为1时，敷设方式为"管埋"
      if (formData.totalHoles === '1' && formData.usedHoles === '1') {
        layingMethodValue = '管埋';
      }
      // 如果断面尺寸中有星号，敷设方式为"管块"，否则为"管埋"
      else if (formData.sectionSize && formData.sectionSize.includes('*')) {
        layingMethodValue = '管块';
      } else {
        layingMethodValue = '管埋';
      }
    }
    
    // 处理井盖信息
    let coverMaterialValue = formData.coverInfo;
    if (coverMaterialValue) {
      // 确保井盖信息格式符合要求
      // 例如：400F -> 尺寸400*400，材质复合
      // F代表复合，T代表铸铁，TO代表砼，G代表钢
      coverMaterialValue = coverMaterialValue.trim().toUpperCase();
      
      // 对于只输入材质信息的情况，添加默认尺寸
      if (['F', 'T', 'TO', 'G'].includes(coverMaterialValue)) {
        coverMaterialValue = '500' + coverMaterialValue;  // 默认尺寸500mm
      }
    }
    
    // 验证断面尺寸格式 - 确保是简化尺寸格式(n*n)，最大为10*10
    let sectionSizeValue = formData.sectionSize;
    if (sectionSizeValue) {
      // 保持原始格式，方便在表格中转换为实际尺寸
      // 转换在DataTable.js中处理，这里只存储原始数据
      sectionSizeValue = sectionSizeValue.replace('×', '*'); // 统一使用*作为分隔符
    }
    
    // 处理表单数据，确保字段格式正确
    const processedFormData = {
      ...formData,
      id: formData.id.toString(), // 确保ID是字符串类型
      wellDepth: formData.wellDepth ? convertCmToM(formData.wellDepth) : undefined,
      leftDepth: formData.leftDepth ? convertCmToM(formData.leftDepth) : undefined,
      rightDepth: formData.rightDepth ? convertCmToM(formData.rightDepth) : undefined,
      topDepth: formData.topDepth ? convertCmToM(formData.topDepth) : undefined,
      bottomDepth: formData.bottomDepth ? convertCmToM(formData.bottomDepth) : undefined,
      // 新增字段
      totalHoles: formData.totalHoles ? formData.totalHoles.toString() : undefined,
      usedHoles: formData.usedHoles ? formData.usedHoles.toString() : undefined,
      cableCount: formData.cableCount ? formData.cableCount.toString() : undefined,
      sectionSize: sectionSizeValue,
      poleSize: formData.poleSize,
      coverMaterial: coverMaterialValue, // 使用处理后的井盖信息
      coverSize: coverMaterialValue, // 同时保存到coverSize字段
      roadLocation: formData.roadLocation,
      layingMethod: layingMethodValue,
      // 添加虚点标记
      isVirtual: !formData.wellDepth || formData.wellDepth === '' || formData.wellDepth === '0.00' || parseFloat(formData.wellDepth) === 0
    };
    
    setLastFormData(newLastFormData);
    localStorage.setItem('lastFormData', JSON.stringify(newLastFormData));
    
    // 如果是编辑现有节点
    if (editingNode) {
      const updatedData = pipelineData.map(node => {
        if (node.key === editingNode.key) {
          return { ...node, ...processedFormData };
        }
        
        // 如果当前编辑的是检修井节点，且有灯杆管径或所在道路更新
        // 检查是否需要更新相关的路灯节点（编号为检修井编号+1的节点）
        if (editingNode.isMainNode && 
           (processedFormData.poleSize || processedFormData.roadLocation || processedFormData.topDepth)) {
          // 检修井ID
          const wellId = editingNode.id;
          
          // 解析节点ID
          const match = wellId.match(/^([a-zA-Z]*)(\d+)$/);
          let lightId;
          
          if (match) {
            // 如果是字符串+数字格式，只增加数字部分
            const prefix = match[1];
            const numPart = parseInt(match[2]);
            lightId = `${prefix}${numPart + 1}`;
          } else {
            // 如果只是数字，简单加1
            const numPart = parseInt(wellId);
            if (!isNaN(numPart)) {
              lightId = (numPart + 1).toString();
            } else {
              // 无法解析的情况，使用原ID + "_light"作为后备
              lightId = wellId + "_light";
            }
          }
          
          // 如果当前节点是对应的路灯节点
          if (!node.isMainNode && node.id === lightId) {
            // 创建更新数据对象
            const lightUpdates = {};
            
            // 如果检修井有灯杆管径，更新路灯的断面尺寸
            if (processedFormData.poleSize) {
              lightUpdates.sectionSize = processedFormData.poleSize;
            }
            
            // 如果检修井有所在道路，更新路灯的所在道路
            if (processedFormData.roadLocation) {
              lightUpdates.roadLocation = processedFormData.roadLocation;
            }
            
            // 如果检修井有上埋深，更新路灯的一般管线点埋深
            if (processedFormData.topDepth) {
              lightUpdates.generalDepth = processedFormData.topDepth;
            }
            
            // 路灯节点的敷设方式固定为"管埋"
            lightUpdates.layingMethod = '管埋';
            
            // 返回更新后的路灯节点
            return { ...node, ...lightUpdates };
          }
        }
        
        return node;
      });
      
      setPipelineData(updatedData);
    } 
    // 如果是添加新节点
    else {
      const position = calculateNodePosition();
      const newNodeKey = `node-${Date.now()}`;
      
      // 保存节点ID
      const nodeId = processedFormData.id;
      
      // 查找最后一个主节点
      const lastMainNode = getLastMainNode(pipelineData);
      
      // 保留用户输入的深度值
      let rightDepth = processedFormData.rightDepth;
      let leftDepth = processedFormData.leftDepth;
      
      // 如果是通过"新建线路"按钮创建的，强制清除左埋深，确保不会与任何现有节点连接
      if (isNewLineCreation) {
        leftDepth = undefined;
        // 确保不会错误地继承前一个节点的rightDepth
        const updatedNodes = pipelineData.map(node => {
          if (node.isMainNode) {
            // 清除所有可能连接到新节点的右埋深
            return { ...node, rightDepth: undefined };
          }
          return node;
        });
        
        // 使用更新后的节点数据
        setPipelineData(updatedNodes);
      }
      // 如果不是新线路，且有最后一个主节点，则处理连接
      else if (lastMainNode && leftDepth) {
          // 更新最后一个节点的右埋深以连接到新节点
          const updatedNodes = pipelineData.map(node => {
            if (node.key === lastMainNode.key) {
              return { 
                ...node, 
                rightDepth: leftDepth // 使用新节点的左埋深作为最后节点的右埋深
              };
            }
            return node;
          });
          
          // 使用更新后的节点数据
          setPipelineData(updatedNodes);
      }
      
      const newData = {
        ...processedFormData,
        leftDepth, // 使用可能修改后的leftDepth
        rightDepth,
        key: newNodeKey,
        x: position.x,
        y: position.y,
        isMainNode: true,
        // 标记这是否是新线路的起始节点
        isNewLineStart: isNewLineCreation || (!leftDepth && pipelineData.length > 0)
      };
      
      // 添加到数据数组
      const newPipelineData = [...pipelineData, newData];
      
      // 解析当前节点ID，计算下一个ID
      let nextId;
      
      // 提取前缀和数字部分
      const match = nodeId.match(/^([a-zA-Z]*)(\d+)$/);
      
      if (match) {
        // 如果ID是字母+数字格式
        const prefix = match[1]; // 字母前缀
        let lastId = parseInt(match[2]); // 数字部分
        
        // 如果有上埋深，添加菱形节点，编号+1
        if (processedFormData.topDepth) {
          const diamondId = `${prefix}${lastId + 1}`;
          const diamondData = {
            id: diamondId,
            key: `diamond-${Date.now()}`,
            x: position.x,
            y: position.y - 100, // 在圆形上方100px
            parentNode: newNodeKey, // 存储父节点的key
            isMainNode: false,
            poleSize: processedFormData.poleSize,
            sectionSize: processedFormData.poleSize,
            roadLocation: processedFormData.roadLocation,
            layingMethod: '管埋',
            generalDepth: processedFormData.topDepth,
            endDepth: processedFormData.topDepth
          };
          newPipelineData.push(diamondData);
          lastId += 1;
        }
        
        // 如果有下埋深，添加方形节点，编号再+1
        if (processedFormData.bottomDepth) {
          const squareId = `${prefix}${lastId + 1}`;
          const squareData = {
            id: squareId,
            key: `square-${Date.now()}`,
            x: position.x,
            y: position.y + 140, // 在圆形下方140px
            parentNode: newNodeKey, // 存储父节点的key
            isMainNode: false,
            roadLocation: processedFormData.roadLocation,
            layingMethod: '管埋',
            generalDepth: processedFormData.bottomDepth,
            endDepth: processedFormData.bottomDepth
          };
          newPipelineData.push(squareData);
          lastId += 1;
        }
        
        nextId = `${prefix}${lastId + 1}`;
      } else {
        // 如果ID只是纯数字
        let lastId = parseInt(nodeId);
        
        // 如果有上埋深，添加菱形节点，编号+1
        if (processedFormData.topDepth) {
          const diamondId = (lastId + 1).toString();
          const diamondData = {
            id: diamondId,
            key: `diamond-${Date.now()}`,
            x: position.x,
            y: position.y - 100, // 在圆形上方100px
            parentNode: newNodeKey, // 存储父节点的key
            isMainNode: false,
            poleSize: processedFormData.poleSize,
            sectionSize: processedFormData.poleSize,
            roadLocation: processedFormData.roadLocation,
            layingMethod: '管埋',
            generalDepth: processedFormData.topDepth,
            endDepth: processedFormData.topDepth
          };
          newPipelineData.push(diamondData);
          lastId += 1;
        }
        
        // 如果有下埋深，添加方形节点，编号+1
        if (processedFormData.bottomDepth) {
          const squareId = (lastId + 1).toString();
          const squareData = {
            id: squareId,
            key: `square-${Date.now()}`,
            x: position.x,
            y: position.y + 140, // 在圆形下方140px
            parentNode: newNodeKey, // 存储父节点的key
            isMainNode: false,
            roadLocation: processedFormData.roadLocation,
            layingMethod: '管埋',
            generalDepth: processedFormData.bottomDepth,
            endDepth: processedFormData.bottomDepth
          };
          newPipelineData.push(squareData);
          lastId += 1;
        }
        
        nextId = lastId + 1;
      }
      
      setPipelineData(newPipelineData);
      setLastMainNode(newData);
      setNextNodeId(nextId);
    }
    
    // 关闭模态框并重置新建线路标记
    setModalVisible(false);
    setIsNewLineCreation(false);
    
    // 添加数据后自动移动到最新节点 - 设置适当延迟确保状态更新
    setTimeout(() => {
      if (pipelineGraphRef.current && !isTableView) {
        pipelineGraphRef.current.centerToLastNode();
      }
    }, 200);  // 调整为200ms延迟
  };
  
  // 获取最大ID的节点（考虑所有类型节点）
  const getLastMainNode = (data) => {
    if (data.length === 0) return null;
    
    // 考虑所有节点，不再过滤isMainNode
    return data.reduce((maxNode, currentNode) => {
      // 提取节点ID的数字部分
      const getNumericId = (id) => {
        const match = id.match(/^([a-zA-Z]*)(\d+)$/);
        if (match) {
          return parseInt(match[2]);
        }
        const numId = parseInt(id);
        return isNaN(numId) ? -1 : numId;
      };
      
      const maxNumId = getNumericId(maxNode.id);
      const currentNumId = getNumericId(currentNode.id);
      
      return currentNumId > maxNumId ? currentNode : maxNode;
    }, data[0]);
  };
  
  // 取消添加/编辑
  const handleCancel = () => {
    setModalVisible(false);
  };
  
  // 撤回上一个添加的节点
  const handleUndo = () => {
    if (pipelineData.length === 0) {
      alert('没有可撤回的操作');
      return;
    }
    
    // 找到最后添加的主节点
    const mainNodes = pipelineData.filter(node => node.isMainNode);
    if (mainNodes.length === 0) return;
    
    const lastNode = mainNodes[mainNodes.length - 1];
    
    // 查找关联的节点
    const nodesToRemove = [lastNode.key];
    
    // 查找所有与该节点关联的子节点(菱形节点和方形节点)
    pipelineData.forEach(node => {
      if (node.parentNode === lastNode.key) {
        nodesToRemove.push(node.key);
      }
    });
    
    // 更新数据数组
    const newPipelineData = pipelineData.filter(node => !nodesToRemove.includes(node.key));
    setPipelineData(newPipelineData);
    
    // 更新最后一个主节点引用和下一个节点ID
    if (newPipelineData.length > 0) {
      // 找到最大ID的节点（考虑所有类型节点）
      const maxIdNode = getLastMainNode(newPipelineData);
      
      // 更新lastMainNode（仍然使用主节点中的最后一个）
      const remainingMainNodes = newPipelineData.filter(node => node.isMainNode);
      if (remainingMainNodes.length > 0) {
        setLastMainNode(remainingMainNodes[remainingMainNodes.length - 1]);
      } else {
        setLastMainNode(null);
      }
      
      // 根据最大ID节点设置下一个节点ID
      const match = maxIdNode.id.match(/^([a-zA-Z]*)(\d+)$/);
      if (match) {
        // 如果ID是字母+数字格式
        const prefix = match[1];
        const numId = parseInt(match[2]);
        setNextNodeId(`${prefix}${numId + 1}`);
      } else {
        // 如果ID是纯数字
        const numId = parseInt(maxIdNode.id);
        if (!isNaN(numId)) {
          setNextNodeId(numId + 1);
        }
      }
    } else {
      setLastMainNode(null);
      setNextNodeId(1);
    }
  };
  
  // 清空全部节点
  const handleClearAll = () => {
    if (pipelineData.length === 0) {
      alert('没有数据可清空');
      return;
    }
    
    // 确认是否清空
    if (window.confirm('确定要清空所有数据吗？此操作不可恢复。')) {
      setPipelineData([]);
      setLastMainNode(null);
      setNextNodeId(1);
      
      // 清除本地存储
      localStorage.removeItem('pipelineData');
    }
  };
  
  // 切换图形/表格视图
  const toggleView = () => {
    if (pipelineData.length === 0) {
      alert('没有可查看的数据');
      return;
    }
    
    // 设置视图切换状态
    setIsViewSwitching(true);
    
    // 延迟执行切换操作，确保有过渡动画
    setTimeout(() => {
      // 切换视图模式
      setIsTableView(!isTableView);
      
      // 如果切换到表格模式，确保重置分页到第一页
      // 在视图切换后，DataTable组件会接收新的pipelineData并自动更新分页信息
      
      // 300ms后解除切换状态
      setTimeout(() => {
        setIsViewSwitching(false);
      }, 300);
      
      // 通知性能监控
      if (window.performance && window.performance.mark) {
        window.performance.mark('view_toggle_complete');
      }
    }, 100);
  };
  
  // 表格数据更新
  const handleDataUpdate = (updatedData) => {
    setPipelineData(updatedData);
  };
  
  // 获取主节点数量
  const getNodeCount = () => {
    return pipelineData.filter(node => node.isMainNode).length;
  };
  
  // 初始值
  const getInitialValues = () => {
    if (editingNode) {
      return editingNode;
    }
    return {
      ...lastFormData
    };
  };
  
  // 添加下载相关状态
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadType, setDownloadType] = useState('visible');
  
  // 显示下载选项对话框
  const showDownloadOptions = () => {
    if (pipelineData.length === 0) {
      alert('没有数据可导出');
      return;
    }
    setDownloadModalVisible(true);
    setDownloadType('visible');
  };
  
  // 处理下载类型选择
  const handleDownloadTypeChange = (e) => {
    setDownloadType(e.target.value);
  };
  
  // 执行下载
  const executeDownload = () => {
    // Close modal
    setDownloadModalVisible(false);
    
    // Check if graph component exists and has data
    if (!pipelineGraphRef.current || pipelineData.length === 0) {
      alert('没有可导出的图表数据');
      return;
    }
    
    // Show loading status
    const loadingDiv = document.createElement('div');
    loadingDiv.style.position = 'absolute';
    loadingDiv.style.top = '50%';
    loadingDiv.style.left = '50%';
    loadingDiv.style.transform = 'translate(-50%, -50%)';
    loadingDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
    loadingDiv.style.padding = '15px 25px';
    loadingDiv.style.borderRadius = '4px';
    loadingDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    loadingDiv.style.zIndex = '1000';
    loadingDiv.style.fontSize = '14px';
    loadingDiv.textContent = '正在准备导出，请稍候...';
    document.body.appendChild(loadingDiv);
    
    try {
      if (downloadType === 'all') {
        // Export full SVG diagram
        const svgData = pipelineGraphRef.current.exportToSVG();
        
        if (!svgData) {
          throw new Error('无法生成SVG数据');
        }
        
        // Update loading message
        loadingDiv.textContent = '正在生成SVG，请稍候...';
        
        // Convert SVG to data URL
        const svgBlob = new Blob([svgData.svgString], {type: 'image/svg+xml;charset=utf-8'});
        const url = URL.createObjectURL(svgBlob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `pipeline-graph-full-${Date.now()}.svg`;
        
        // Remove loading indicator and trigger download
        document.body.removeChild(loadingDiv);
        link.click();
        
        // Clean up URL object
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      } else {
        // For visible area only, still use html2canvas for raster image
        import('html2canvas').then(({ default: html2canvas }) => {
          // Configure options
          let options = {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
          };
          
          const graphInfo = pipelineGraphRef.current.preparingForDownload('visible');
          
          html2canvas(graphInfo.containerElement, options).then(canvas => {
            loadingDiv.textContent = '准备下载...';
            
            setTimeout(() => {
              document.body.removeChild(loadingDiv);
              
              canvas.toBlob(blob => {
                const link = document.createElement('a');
                link.download = `pipeline-graph-visible-${Date.now()}.png`;
                link.href = URL.createObjectURL(blob);
                link.click();
                
                setTimeout(() => {
                  URL.revokeObjectURL(link.href);
                }, 100);
              }, 'image/png');
            }, 500);
          }).catch(error => {
            if (document.body.contains(loadingDiv)) {
              document.body.removeChild(loadingDiv);
            }
            console.error('导出图像失败：', error);
            alert('导出图像失败，请重试');
          });
        });
      }
    } catch (error) {
      console.error('导出图表失败:', error);
      if (document.body.contains(loadingDiv)) {
        document.body.removeChild(loadingDiv);
      }
      alert('导出失败，请重试');
    }
  };
  
  // 如果应用还未准备好，显示加载中
  if (!appReady) {
    return (
      <div className="app-loading">
        <h2>翰达锤子工具正在加载中...</h2>
        <Spin size="large" />
      </div>
    );
  }
  
  return (
    <div className={`app-container ${isLowPerformanceBrowser ? 'low-performance-mode' : ''}`}>
      <header className="header">
        <h1>翰达锤子工具</h1>
        <div className="version">V1.0.2</div>
      </header>
      
      <div className="content">
        <Suspense fallback={<div className="component-loading"><Spin /></div>}>
          {!isTableView ? (
            <PipelineGraph 
              pipelineData={pipelineData}
              lastMainNode={lastMainNode}
              onNodeClick={handleNodeClick}
              nodeCount={getNodeCount()}
              ref={pipelineGraphRef}
            />
          ) : (
            <DataTable 
              pipelineData={pipelineData}
              onNodeClick={handleNodeClick}
              onDataUpdate={handleDataUpdate}
            />
          )}
        </Suspense>
      </div>
      
      <div className="button-group" style={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* 左侧区域 */}
        <div>
          <Button danger onClick={handleClearAll} icon={<ClearOutlined />} disabled={pipelineData.length === 0}>
            {isMobile ? '' : '清空'}
          </Button>
          {/* 添加下载按钮 - 仅在图形视图模式显示 */}
          {!isTableView && (
            <Button 
              onClick={showDownloadOptions} 
              icon={<DownloadOutlined />} 
              style={{ marginLeft: 8 }}
              disabled={pipelineData.length === 0}
            >
              {isMobile ? '' : '下载图片'}
            </Button>
          )}
        </div>
        
        {/* 中间区域 - 操作按钮 */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button onClick={showNewLineModal} disabled={isTableView || isViewSwitching}>
            {isMobile ? 'NL' : '新建线路'}
          </Button>
          <Button onClick={handleUndo} style={{ marginLeft: 8 }} icon={<UndoOutlined />} disabled={pipelineData.length === 0 || isViewSwitching}>
            {isMobile ? '' : '撤销'}
          </Button>
          <Button type="primary" onClick={showAddModal} style={{ marginLeft: 8 }} disabled={isTableView || isViewSwitching}>
            {isMobile ? '+' : '添加节点'}
          </Button>
        </div>
        
        {/* 右侧区域 */}
        <div>
          <Button 
            onClick={toggleView} 
            icon={isTableView ? <FundOutlined /> : <AppstoreOutlined />}
            disabled={isViewSwitching}
          >
            {isMobile ? '' : (isTableView ? '图形视图' : '表格视图')}
          </Button>
        </div>
      </div>
      
      {/* 添加/编辑数据模态框 - 确保不在Suspense内部 */}
      {modalVisible && (
        <AddDataModal
          visible={modalVisible}
          title={modalTitle}
          onCancel={handleCancel}
          onSubmit={handleFormSubmit}
          editingNode={editingNode}
          nextNodeId={nextNodeId}
          lastFormData={lastFormData}
        />
      )}
      
      {/* 下载选项模态框 */}
      <Modal
        title="选择下载选项"
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        onOk={executeDownload}
        okText="下载"
        cancelText="取消"
      >
        <p>请选择要下载的区域:</p>
        <Radio.Group onChange={handleDownloadTypeChange} value={downloadType}>
          <Space direction="vertical">
            <Radio value="visible">当前视图区域 (PNG格式)</Radio>
            <Radio value="all">完整图表 (SVG矢量格式，包含所有节点)</Radio>
          </Space>
        </Radio.Group>
        {downloadType === 'all' && (
          <div style={{ marginTop: 12, color: '#1890ff' }}>
            SVG矢量图可无损缩放，适合高质量打印和后期编辑
          </div>
        )}
      </Modal>
    </div>
  );
};

export default App;
